import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    fetchInvitations,
    resendInvitation,
    updateInvitationStatus,
} from "../services/api";
import InvitationLinkDisplay from "./InvitationLinkDisplay";
import "../styles/invitations.css";

const STATUS_TABS = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Completed", value: "completed" },
    { label: "Expired", value: "expired" },
    { label: "Cancelled", value: "cancelled" },
];

const STATUS_COLORS = {
    pending: "pending",
    completed: "completed",
    expired: "expired",
    cancelled: "cancelled",
};

const formatDate = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "—";
    }
    return parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "—";
    }
    return parsed.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function InvitationsPage() {
    const navigate = useNavigate();
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [invitations, setInvitations] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortKey, setSortKey] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [expandedId, setExpandedId] = useState(null);
    const [selectedInvitation, setSelectedInvitation] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const limit = 50;
    const pageCount = Math.max(1, Math.ceil(totalCount / limit));

    const loadInvitations = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await fetchInvitations({
                status: selectedStatus,
                limit,
                offset: page * limit,
            });
            setInvitations(result.data || []);
            setTotalCount(result.count || 0);
        } catch (err) {
            setError(err?.message || "Unable to load invitations.");
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, page]);

    useEffect(() => {
        loadInvitations();
    }, [loadInvitations]);

    const filteredInvitations = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return invitations;
        return invitations.filter((invitation) => {
            const clientName = `${invitation.client_first_name || ""} ${invitation.client_last_name || ""}`.toLowerCase();
            return (
                invitation.client_email?.toLowerCase().includes(term) ||
                invitation.company?.toLowerCase().includes(term) ||
                invitation.provider?.toLowerCase().includes(term) ||
                clientName.includes(term)
            );
        });
    }, [invitations, searchTerm]);

    const sortedInvitations = useMemo(() => {
        const sorted = [...filteredInvitations];
        sorted.sort((a, b) => {
            const aValue = a[sortKey];
            const bValue = b[sortKey];
            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            if (sortKey.includes("at")) {
                const aTime = new Date(aValue).getTime();
                const bTime = new Date(bValue).getTime();
                return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
            }
            const comparison = String(aValue).localeCompare(String(bValue));
            return sortDirection === "asc" ? comparison : -comparison;
        });
        return sorted;
    }, [filteredInvitations, sortKey, sortDirection]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDirection("asc");
        }
    };

    const handleResend = async (invitationId) => {
        setActionLoadingId(invitationId);
        setError("");
        setNotice("");
        try {
            const result = await resendInvitation(invitationId);
            await loadInvitations();
            if (result.deliveryStatus === "accepted") {
                setNotice(result.renewed
                    ? "Invitation renewed. The reminder email was accepted for delivery."
                    : "The reminder email was accepted for delivery.");
            } else {
                setError("The invitation is active, but the reminder email was not confirmed as sent. Use View link to copy and share the valid link securely.");
            }
        } catch (err) {
            setError(err?.message || "Unable to send the reminder email.");
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleCancel = async (invitationId) => {
        setActionLoadingId(invitationId);
        setError("");
        setNotice("");
        try {
            await updateInvitationStatus(invitationId, "cancelled");
            await loadInvitations();
        } catch (err) {
            setError(err?.message || "Unable to cancel invitation.");
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleRowClick = (invitationId) => {
        setExpandedId((prev) => (prev === invitationId ? null : invitationId));
    };

    return (
        <main className="invitations-page">
            <div className="content-shell">
                <div className="invitation-card">
                    <div className="invitation-card-header invitations-header">
                        <div>
                            <h1>Client invitations</h1>
                            <p>Manage invitation links and track completion.</p>
                        </div>
                        <button
                            className="btn"
                            onClick={() => navigate("/invitations/create")}
                        >
                            Create new
                        </button>
                    </div>

                    <div className="invitations-toolbar">
                        <div className="invitations-filters">
                            {STATUS_TABS.map((tab) => (
                                <button
                                    key={tab.value}
                                    className={`filter-btn ${
                                        selectedStatus === tab.value
                                            ? "active"
                                            : ""
                                    }`}
                                    onClick={() => {
                                        setSelectedStatus(tab.value);
                                        setPage(0);
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <input
                            className="search-input"
                            placeholder="Search by client or company"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </div>

                    {error && <div className="alert error-alert">{error}</div>}
                    {notice && <div className="alert success-alert">{notice}</div>}

                    {loading ? (
                        <div className="empty-state">Loading invitations...</div>
                    ) : sortedInvitations.length === 0 ? (
                        <div className="empty-state">
                            No invitations found for this filter.
                        </div>
                    ) : (
                        <div className="invitations-table">
                            <div className="table-header">
                                <button onClick={() => toggleSort("client_first_name")}>
                                    Client
                                </button>
                                <button onClick={() => toggleSort("client_email")}>
                                    Email
                                </button>
                                <button onClick={() => toggleSort("company")}>
                                    Company
                                </button>
                                <button onClick={() => toggleSort("provider")}>
                                    Provider
                                </button>
                                <button onClick={() => toggleSort("status")}>
                                    Status
                                </button>
                                <button onClick={() => toggleSort("created_at")}>
                                    Created
                                </button>
                                <button onClick={() => toggleSort("expires_at")}>
                                    Expires
                                </button>
                                <span>Actions</span>
                            </div>
                            {sortedInvitations.map((invitation) => {
                                const clientName = `${invitation.client_first_name || ""} ${invitation.client_last_name || ""}`.trim();
                                return (
                                    <div key={invitation.id} className="table-row">
                                        <button
                                            className="row-cell clickable"
                                            onClick={() =>
                                                handleRowClick(invitation.id)
                                            }
                                        >
                                            {clientName || "—"}
                                        </button>
                                        <span className="row-cell">
                                            {invitation.client_email}
                                        </span>
                                        <span className="row-cell">
                                            {invitation.company}
                                        </span>
                                        <span className="row-cell">
                                            {invitation.provider}
                                        </span>
                                        <span className="row-cell">
                                            <span
                                                className={`invitation-status-badge ${STATUS_COLORS[invitation.status]}`}
                                            >
                                                {invitation.status}
                                            </span>
                                        </span>
                                        <span className="row-cell">
                                            {formatDate(invitation.created_at)}
                                        </span>
                                        <span className="row-cell">
                                            {invitation.status === "completed"
                                                ? formatDate(invitation.completed_at)
                                                : formatDate(invitation.expires_at)}
                                        </span>
                                        <div className="row-cell invitation-actions">
                                            <button
                                                className="btn secondary-btn"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setSelectedInvitation(
                                                        invitation
                                                    );
                                                }}
                                            >
                                                View link
                                            </button>
                                            {(invitation.status === "pending" || invitation.status === "expired") && (
                                                <button
                                                    className="btn ghost-btn"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleResend(invitation.id);
                                                    }}
                                                    disabled={
                                                        actionLoadingId ===
                                                        invitation.id
                                                    }
                                                >
                                                    {actionLoadingId === invitation.id
                                                        ? "Sending…"
                                                        : invitation.status === "expired"
                                                          ? "Renew & send reminder"
                                                          : "Send reminder"}
                                                </button>
                                            )}
                                            {invitation.status === "pending" && (
                                                <button
                                                    className="btn ghost-btn"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleCancel(invitation.id);
                                                    }}
                                                    disabled={
                                                        actionLoadingId ===
                                                        invitation.id
                                                    }
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                        {expandedId === invitation.id && (
                                            <div className="invitation-details">
                                                <div>
                                                    <strong>Created by:</strong>{" "}
                                                    {invitation.created_by_profile
                                                        ? invitation.created_by_profile.full_name ||
                                                          invitation.created_by_profile.email
                                                        : "—"}
                                                </div>
                                                <div>
                                                    <strong>Expires:</strong>{" "}
                                                    {formatDateTime(
                                                        invitation.expires_at
                                                    )}
                                                </div>
                                                <div>
                                                    <strong>Completed:</strong>{" "}
                                                    {formatDateTime(
                                                        invitation.completed_at
                                                    )}
                                                </div>
                                                <div>
                                                    <strong>Credential ID:</strong>{" "}
                                                    {invitation.credential_id || "—"}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="pagination">
                        <button
                            className="btn ghost-btn"
                            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                            disabled={page === 0}
                        >
                            Previous
                        </button>
                        <span>
                            Page {page + 1} of {pageCount}
                        </span>
                        <button
                            className="btn ghost-btn"
                            onClick={() =>
                                setPage((prev) =>
                                    prev + 1 < pageCount ? prev + 1 : prev
                                )
                            }
                            disabled={page + 1 >= pageCount}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            {selectedInvitation && (
                <InvitationLinkDisplay
                    invitationId={selectedInvitation.id}
                    onClose={() => setSelectedInvitation(null)}
                />
            )}
        </main>
    );
}
